import { useContext, lazy, Suspense } from 'react';
import { Redirect, Route, Switch } from 'react-router-dom';
import { UserContext } from './providers/UserProvider';
import { Roles } from './models/Roles';
import { Login } from './pages/basic/Login';
import { PageNotFound } from './pages/basic/PageNotFound';
import { XimiSSOCallback } from './pages/ximi/XimiSSOCallback';
import { XimiSelectAccount } from './pages/ximi/XimiSelectAccount';
import { XimiSsoLoginManualEntry } from './pages/ximi/XimiSsoLoginManualEntry';
import {
    XIMI_LOGIN_MANUAL_ENTRY_PATH,
    XIMI_LOGIN_SELECT_ACCOUNT_PATH,
    XIMI_SSO_CALLBACK_PATH
} from './routePaths';
import { XIMI_LOGIN_ENABLED } from './config';
import { usePersistXimiAccount } from './utilities/ximi/usePersistXimiAccount';
import { useXimiDeeplink } from './utilities/ximi/useXimiDeeplink';
import { useXimiStimulinSignIn } from './utilities/ximi/useXimiStimulinSignIn';
import { Register } from './pages/basic/Register';
import { Loader } from './components/Loader';
import { useAppUpdateCheck } from './hooks/useAppUpdateCheck';

// Lazy loading pages to reduce the initial bundle
const Tourbillon = lazy(() => import('./pages/games/Tourbillon'));
const TourbillonLevels = lazy(() => import('./pages/games/Tourbillon/levels'));
const PingPong = lazy(() => import('./pages/games/PingPong'));
const PingPongLevels = lazy(() => import('./pages/games/PingPong/levels'));
const Duo = lazy(() => import('./pages/games/Memory'));
const DuoLevels = lazy(() => import('./pages/games/Memory/levels'));
const GrillesEtChiffres = lazy(() => import('./pages/games/Sudok'));
const GrillesEtChiffresLevels = lazy(() => import('./pages/games/Sudok/levels'));
const EnMorceaux = lazy(() => import('./pages/games/Puzzle'));
const EnMorceauxLevels = lazy(() => import('./pages/games/Puzzle/levels'));
const UnPeuDOrdre = lazy(() => import('./pages/games/Reordon'));
const UnPeuDOrdreLevels = lazy(() => import('./pages/games/Reordon/levels'));
const MeliMots = lazy(() => import('./pages/games/MeliMots'));
const MeliMotsLevels = lazy(() => import('./pages/games/MeliMots/levels'));
const BonTitre = lazy(() => import('./pages/games/Quizz'));
const BonTitreLevels = lazy(() => import('./pages/games/Quizz/levels'));
const CodeExpress = lazy(() => import('./pages/games/CodeExpress'));
const CodeExpressLevels = lazy(() => import('./pages/games/CodeExpress/levels'));
const Home = lazy(() => import('./pages/Home'));
const News = lazy(() => import('./pages/News'));
const Games = lazy(() => import('./pages/Games'));
const GamesTheme = lazy(() => import('./pages/GamesTheme'));
const Album = lazy(() => import('./pages/Album'));
const SparklesTest = lazy(() => import('./pages/SparklesTest'));

function PatientRoutes(): JSX.Element {
    return <Switch>{/* <Route path="/home" exact component={AdminDashboard} /> */}</Switch>;
}
function FamilyRoutes(): JSX.Element {
    return <Switch>{/* <Route path="/home" exact component={AdminDashboard} /> */}</Switch>;
}
function TherapistRoutes(): JSX.Element {
    return <Switch>{/* <Route path="/home" exact component={AdminDashboard} /> */}</Switch>;
}
function AdminRoutes(): JSX.Element {
    return <Switch>{/* <Route path="/home" exact component={AdminDashboard} /> */}</Switch>;
}

function setUserRoutes(): JSX.Element {
    const userDataProp = useContext(UserContext);
    const user = userDataProp?.user;
    if (user === null) {
        document.documentElement.style.setProperty('--ion-font-family', 'Poppins-Regular');
        return <Redirect to="/login" />;
    }
    const role = user?.type;
    let mappedRole: string | undefined;
    switch (role) {
        case 'USER':
            mappedRole = Roles.SENIOR;
            break;
        case 'FAMILY':
            mappedRole = Roles.FAMILY;
            break;
        case 'PRO':
            mappedRole = Roles.PRO;
            break;
        case 'ADMIN':
            mappedRole = Roles.ADMIN;
            break;
        default:
            mappedRole = undefined;
    }
    switch (mappedRole) {
        case Roles.SENIOR:
            document.documentElement.style.setProperty(
                '--ion-font-family',
                'Poppins-Regular, sans-serif'
            );
            return PatientRoutes();
        case Roles.FAMILY:
            document.documentElement.style.setProperty('--ion-font-family', 'Poppins-Regular');
            return FamilyRoutes();
        case Roles.PRO:
            document.documentElement.style.setProperty('--ion-font-family', 'Poppins-Regular');
            return TherapistRoutes();
        case Roles.ADMIN:
            document.documentElement.style.setProperty('--ion-font-family', 'Poppins-Regular');
            return AdminRoutes();
        default:
            document.documentElement.style.setProperty('--ion-font-family', 'Poppins-Regular');
            return <Route path="/401" component={PageNotFound} />;
    }
}

export function Router(): JSX.Element {
    usePersistXimiAccount();
    useXimiDeeplink();
    useXimiStimulinSignIn(); // SSO Ximi → échange /v2/auth/ximi → tokens Stimul'In + /home
    useAppUpdateCheck();

    return (
        <>
            <Suspense
                fallback={
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: '100vh'
                        }}>
                        <Loader />
                    </div>
                }>
                <Switch>
                    <Route path="/" exact>
                        <Redirect to="/home" />
                    </Route>
                    <Route path="/home" exact component={Home} />
                    <Route path="/login" exact component={Login} />
                    <Route path="/login/:email" exact component={Login} />
                    <Route path="/register" exact component={Register} />
                    <Route path="/register/:code" exact component={Register} />
                    {XIMI_LOGIN_ENABLED && (
                        <Route path={XIMI_SSO_CALLBACK_PATH} exact component={XimiSSOCallback} />
                    )}
                    {XIMI_LOGIN_ENABLED && (
                        <Route
                            path={XIMI_LOGIN_SELECT_ACCOUNT_PATH}
                            exact
                            component={XimiSelectAccount}
                        />
                    )}
                    {XIMI_LOGIN_ENABLED && (
                        <Route
                            path={XIMI_LOGIN_MANUAL_ENTRY_PATH}
                            exact
                            component={XimiSsoLoginManualEntry}
                        />
                    )}
                    <Route path="/profile" exact component={Home} />
                    <Route path="/news" exact component={News} />
                    <Route path="/games" exact component={Games} />
                    <Route path="/:game/theme" exact component={GamesTheme} />
                    <Route path="/album" exact component={Album} />
                    {/* Each game's level page belongs to the game engine (GameLevels). */}
                    <Route path="/code-express/:id/lvl" exact component={CodeExpressLevels} />
                    <Route path="/melimots/:id/lvl" exact component={MeliMotsLevels} />
                    <Route path="/quizz/:id/lvl" exact component={BonTitreLevels} />
                    <Route path="/memory/:id/lvl" exact component={DuoLevels} />
                    <Route path="/reordon/:id/lvl" exact component={UnPeuDOrdreLevels} />
                    <Route path="/puzzle/:id/lvl" exact component={EnMorceauxLevels} />
                    <Route path="/sudok/:id/lvl" exact component={GrillesEtChiffresLevels} />
                    <Route path="/tourbillon/:id/lvl" exact component={TourbillonLevels} />
                    <Route path="/pingpong/:id/lvl" exact component={PingPongLevels} />
                    <Route path="/sudok/:id/lvl/:lvlId" exact component={GrillesEtChiffres} />
                    <Route path="/tourbillon/:id/lvl/:lvlId" exact component={Tourbillon} />
                    <Route path="/memory/:id/lvl/:lvlId" exact component={Duo} />
                    <Route path="/pingpong/:id/lvl/:lvlId" exact component={PingPong} />
                    <Route path="/puzzle/:id/lvl/:lvlId" exact component={EnMorceaux} />
                    <Route path="/reordon/:id/lvl/:lvlId" exact component={UnPeuDOrdre} />
                    <Route path="/melimots/:id/lvl/:lvlId" exact component={MeliMots} />
                    <Route path="/quizz/:id/lvl/:lvlId" exact component={BonTitre} />
                    <Route path="/code-express/:id/lvl/:lvlId" exact component={CodeExpress} />
                    <Route path="/sparkles-test" exact component={SparklesTest} />
                    {setUserRoutes()}
                    <Route path="/404" component={PageNotFound} />;
                    <Route path="*">
                        <Redirect to="/404" />
                    </Route>
                </Switch>
            </Suspense>
        </>
    );
}
